import axiosInstance from "@/api/axiosConfig";
import type { AgentScope } from "@/features/agents/types";

import type { AttentionItem } from "./types";

type Scope = Pick<AgentScope, "kind" | "id">;

const threadBase = (scope: Scope, threadId: string): string =>
  `/agents/${scope.kind}/${encodeURIComponent(
    scope.id
  )}/threads/${encodeURIComponent(threadId)}`;

export const AttentionAPI = {
  async list(): Promise<AttentionItem[]> {
    const { data } = await axiosInstance.get<{ items?: AttentionItem[] }>(
      "/agents/attention"
    );
    return data.items ?? [];
  },
  /** I've seen the outcome: clears finished/failed for me and stops my email. */
  async markSeen(scope: Scope, threadId: string): Promise<void> {
    await axiosInstance.post(`${threadBase(scope, threadId)}/attention/seen`);
  },
  /** Wave it off: it leaves my list until the thread raises something new. */
  async dismiss(scope: Scope, threadId: string): Promise<void> {
    await axiosInstance.post(
      `${threadBase(scope, threadId)}/attention/dismiss`
    );
  },
  /** Skip the pending question; the agent carries on without the answers. */
  async skip(scope: Scope, threadId: string): Promise<void> {
    await axiosInstance.post(`${threadBase(scope, threadId)}/attention/skip`);
  },
  async snooze(
    scope: Scope,
    threadId: string,
    minutes: number
  ): Promise<string | null> {
    const { data } = await axiosInstance.post<{
      snoozed_until?: string | null;
    }>(`${threadBase(scope, threadId)}/attention/snooze`, { minutes });
    return data.snoozed_until ?? null;
  },
  /** The thread is on screen (visible and focused); sent every `WATCH_HEARTBEAT_MS`. */
  async watch(scope: Scope, threadId: string): Promise<void> {
    await axiosInstance.post(`${threadBase(scope, threadId)}/attention/watch`);
  }
};

export const WATCH_HEARTBEAT_MS = 30_000;
export const SNOOZE_MINUTES = 15;
